import type { QueryClient, QueryKey } from '@tanstack/react-query';

/**
 * A trigger that refetches `queryKey` for a live stream whose frames are
 * invalidations — the Activities feed's, on web and mobile alike.
 *
 * Coalesced by construction, not by a timer: at most ONE refetch in flight and
 * at most one queued behind it. The first frame refetches at once — cancelling
 * a fetch that began before it, so the answer postdates the change. Frames that
 * land while that refetch runs only note that it may already be behind, and a
 * single refetch follows when it settles. A burst of frames therefore costs two
 * reads rather than a cancel-and-restart per frame, and every frame is still
 * followed by a read that began after it.
 */
export function coalescedInvalidation(qc: QueryClient, queryKey: QueryKey): () => void {
  let refetching = false;
  let behind = false;
  const refetch = (): void => {
    if (refetching) {
      behind = true;
      return;
    }
    refetching = true;
    void qc.invalidateQueries({ queryKey }).then(() => {
      refetching = false;
      if (!behind) return;
      behind = false;
      refetch();
    });
  };
  return refetch;
}
